// construction new/frontend/src/pages/Supervisor/LogRawMaterials.js
import React, { useEffect, useState,  useCallback } from 'react'; 
import API from '../../api/axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Table from '../../components/common/Table';
import Modal from '../../components/common/Modal';
import MaterialLogForm from '../../components/supervisor/MaterialLogForm';
import { FaFilter, FaPlus, FaEdit, FaTrash } from 'react-icons/fa'; 
import { useParams } from 'react-router-dom';
import Spinner from '../../components/common/Spinner';

const LogRawMaterials = () => {
  // const { user } = useContext(AuthContext); // Removed: 'user' is assigned a value but never used
  const { siteId: urlSiteId } = useParams();
  const [materialLogs, setMaterialLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [filters, setFilters] = useState({ siteId: urlSiteId || '', material: '', startDate: '', endDate: '' });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMaterialLog, setSelectedMaterialLog] = useState(null);

  // Wrapped fetchMaterialLogs with useCallback
  const fetchMaterialLogs = useCallback(async (currentFilters) => { // Accept filters as argument
    setLoading(true);
    try {
      const params = new URLSearchParams(currentFilters).toString(); // Use currentFilters
      const res = await API.get(`/materials?${params}`);
      setMaterialLogs(res.data);
    } catch (error) {
      toast.error('Failed to load material logs.');
      console.error('Error fetching material logs:', error);
    } finally {
      setLoading(false);
    }
  }, []);  // Dependency array includes 'filters' as it's used inside

  // Wrapped fetchMySitesAndWorkers with useCallback
  const fetchMySitesAndWorkers = useCallback(async () => {
    setLoading(true); // Add this line
    try {
      const sitesRes = await API.get('/supervisor/my-sites');
      setSites(sitesRes.data);
  
      let initialSiteId = urlSiteId;
      if (!urlSiteId && sitesRes.data.length === 1) {
        initialSiteId = sitesRes.data[0]._id;
      }
      
      // Replace the existing setFilters block with this
      setFilters(prev => {
        const newFilters = { ...prev, siteId: initialSiteId || prev.siteId };
        fetchMaterialLogs(newFilters); // Trigger fetch with the new filters
        return newFilters;
      });
  
    } catch (error) {
      toast.error('Failed to load sites or workers for filter.');
      console.error('Error fetching sites/workers:', error);
      setLoading(false); // Ensure loading is set to false on error
    }
    // Remove the finally block if it existed here, as setLoading is now handled above or in fetchMaterialLogs
  }, [urlSiteId, fetchMaterialLogs]);

  useEffect(() => {
    fetchMySitesAndWorkers();
  }, [fetchMySitesAndWorkers]);

    useEffect(() => {
      fetchMaterialLogs(filters); 
    }, [filters, fetchMaterialLogs]);// Added fetchMaterialLogs and loading

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => fetchMaterialLogs(filters);

  const resetFilters = () => {
    setFilters({ siteId: urlSiteId || '', material: '', startDate: '', endDate: '' });
  };

  const handleAddLog = () => {
    if (!filters.siteId) {
      toast.error('Please select a site from the filter to add a new material log.');
      return;
    }
    setSelectedMaterialLog(null);
    setIsModalOpen(true);
  };

  const handleEditLog = (log) => {
    setSelectedMaterialLog(log);
    setIsModalOpen(true);
  };

  const handleDeleteLog = async (id) => {
    if (window.confirm('Are you sure you want to delete this material log?')) {
      try {
        await API.delete(`/materials/${id}`);
        toast.success('Material log deleted successfully!');
        fetchMaterialLogs(filters);
      } catch (error) {
        toast.error(`Failed to delete material log: ${error.response?.data?.message || error.message}`);
        console.error('Error deleting material log:', error);
      }
    }
  };

  const handleSaveLog = () => {
    fetchMaterialLogs(filters);
    setIsModalOpen(false);
  };

  const tableHeaders = ['Site', 'Material', 'Brand', 'Quantity', 'Unit', 'Price/Unit', 'Total Cost', 'Flight Charge', 'Date', 'Recorded By', 'Actions'];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-100px)]">
        <Spinner size="lg" type="logo" color="#2F3C7E" />
        <span className="ml-3 mt-4 text-lg text-gray-700">Loading raw material logs...</span>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6">Log & View Raw Materials</h2>

      <div className="mb-6 p-4 bg-gray-50 rounded-lg shadow-sm">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-4 flex items-center">
          <FaFilter className="mr-2 text-indigo-600" /> Filters
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Site</label>
            <select
              name="siteId"
              value={filters.siteId}
              onChange={handleFilterChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              disabled={!!urlSiteId}
            >
              <option value="">All My Sites</option>
              {sites.map((site) => (
                <option key={site._id} value={site._id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Material Name</label>
            <input
              type="text"
              name="material"
              value={filters.material}
              onChange={handleFilterChange}
              placeholder="e.g., Cement"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            onClick={applyFilters}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Apply Filters
          </button>
          <button
            onClick={resetFilters}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
          >
            Reset Filters
          </button>
        </div>
      </div>

      <div className="flex justify-end items-center mb-4">
        <button
          onClick={handleAddLog}
          className={`flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md shadow hover:bg-indigo-700 ${!filters.siteId ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={!filters.siteId}
        >
          <FaPlus className="mr-2" /> Log New Material
        </button>
      </div>

      <Table
        headers={tableHeaders}
        data={materialLogs}
        emptyMessage="No material logs found for the selected criteria."
        renderRow={(log) => {
          const totalCostIncludingFlight = (
            (typeof log.total === 'number' ? log.total : 0) +
            (typeof log.flightCharge === 'number' ? log.flightCharge : 0)
          ).toFixed(2);

          return (
            <tr key={log._id} className="hover:bg-gray-50">
              <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm">
                {log.siteId?.name || 'N/A'}
              </td>
              <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm">
                {log.material}
              </td>
              <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm">
                {log.brand || 'N/A'}
              </td>
              <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm">
                {log.quantity}
              </td>
              <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm">
                {log.unit}
              </td>
              <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm">
                ₹{typeof log.pricePerUnit === 'number' ? log.pricePerUnit.toFixed(2) : 'N/A'}
              </td>
              <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm">
                ₹{totalCostIncludingFlight}
              </td>
              <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm">
                ₹{typeof log.flightCharge === 'number' ? log.flightCharge.toFixed(2) : 'N/A'}
              </td>
              <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm">
                {log.date ? new Date(log.date).toLocaleDateString('en-GB') : 'N/A'}
              </td>
              <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm">
                {log.recordedBy?.name || 'N/A'}
              </td>
              <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm flex space-x-2">
                <button
                  onClick={() => handleEditLog(log)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <FaEdit />
                </button>
                <button
                  onClick={() => handleDeleteLog(log._id)}
                  className="text-red-600 hover:text-red-800"
                >
                  <FaTrash />
                </button>
              </td>
            </tr>
          );
        }}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedMaterialLog ? 'Edit Material Entry' : 'Log New Material'}
        size="md"
      >
        <MaterialLogForm
          entryToEdit={selectedMaterialLog}
          onMaterialLogAdded={handleSaveLog}
          onClose={() => setIsModalOpen(false)}
          siteId={filters.siteId || urlSiteId}
        />
      </Modal>

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
    </div>
  );
};

export default LogRawMaterials;
